"use client";

import React, { useState } from "react";

export interface SankalpProgramData {
  programNameEn: string;
  programNameHi: string;
  totalDays: 21 | 41;
  currentDay: number;
  mantraSanskrit: string;
  mantraTranslationEn: string;
  mantraTranslationHi: string;
  targetCount: number; // 108
  deityEn: string;
  deityHi: string;
  guidelinesEn: string[];
  guidelinesHi: string[];
}

export interface SankalpProgramTrackerProps {
  data: SankalpProgramData;
  locale?: "en" | "hi";
}

export default function SankalpProgramTracker({ data, locale = "en" }: SankalpProgramTrackerProps) {
  const isHi = locale === "hi";
  const [japaCount, setJapaCount] = useState<number>(0);
  const [isCompletedToday, setIsCompletedToday] = useState<boolean>(false);

  const incrementJapa = () => {
    setJapaCount((prev) => {
      const next = Math.min(prev + 1, data.targetCount);
      if (next === data.targetCount) {
        setIsCompletedToday(true);
      }
      return next;
    });
  };

  const progressPercent = Math.round((data.currentDay / data.totalDays) * 100);
  const progressBarStyle = { width: `${progressPercent}%` };

  return (
    <div
      className="w-full rounded-2xl border p-5 sm:p-6 transition-all bg-surface-raised border-border-default shadow-card"
      aria-label={
        isHi ? "वैदिक संकल्प एवं मंत्र साधना ट्रैकर" : "21-Day Vedic Sankalp & Upay Tracker"
      }
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-xl">🪔</span>
            <h3 className="text-base sm:text-lg font-semibold tracking-tight text-text-primary">
              {isHi ? data.programNameHi : data.programNameEn}
            </h3>
          </div>
          <p className="text-xs sm:text-sm mt-0.5 text-text-secondary">
            {isHi
              ? `इष्ट देव: ${data.deityHi} · ${data.totalDays} दिवसीय वैदिक संकल्प अनुष्ठान`
              : `Deity: ${data.deityEn} · ${data.totalDays}-Day Vedic Sankalp Program`}
          </p>
        </div>

        {/* Day Badge */}
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-surface-brand-subtle text-text-primary border border-border-interactive self-start sm:self-auto">
          <span>📅</span>
          <span>
            {isHi
              ? `दिवस ${data.currentDay} / ${data.totalDays}`
              : `Day ${data.currentDay} of ${data.totalDays}`}
          </span>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="my-4">
        <div className="flex justify-between text-xs text-text-secondary mb-1">
          <span>{isHi ? "संकल्प प्रगति" : "Sankalp Journey Progress"}</span>
          <span className="font-mono font-bold">{progressPercent}%</span>
        </div>
        <div className="w-full bg-surface-pressed rounded-full h-2.5 overflow-hidden">
          <div
            className="h-full bg-intent-brand rounded-full transition-all duration-500"
            style={progressBarStyle}
          />
        </div>
      </div>

      {/* Daily Mantra Japa Counter */}
      <div className="p-4 rounded-xl border border-border-interactive bg-surface-brand-subtle my-4 text-center">
        <p className="font-serif text-sm sm:text-base font-semibold text-text-primary mb-1">
          {data.mantraSanskrit}
        </p>
        <p className="text-xs text-text-secondary mb-3">
          {isHi ? data.mantraTranslationHi : data.mantraTranslationEn}
        </p>

        {/* Counter Button */}
        <div className="flex flex-col items-center gap-2">
          <button
            type="button"
            onClick={incrementJapa}
            disabled={isCompletedToday}
            className={`px-6 py-2.5 rounded-xl font-bold text-sm transition-all cursor-pointer shadow-xs active:scale-95 ${
              isCompletedToday
                ? "bg-success-strong text-text-on-brand cursor-default"
                : "bg-surface-brand-strong hover:bg-surface-brand-strong-hover text-text-on-brand"
            }`}
          >
            {isCompletedToday
              ? isHi
                ? "✓ आज का जप पूर्ण (108 पूर्ण)"
                : "✓ 108 Repetitions Completed Today"
              : isHi
                ? `मंत्र जप माला स्पर्श (${japaCount} / ${data.targetCount})`
                : `Tap Japa Bead (${japaCount} / ${data.targetCount})`}
          </button>
        </div>
      </div>

      {/* Daily Guidelines */}
      {data.guidelinesEn.length > 0 && (
        <div className="p-3.5 rounded-xl border text-xs bg-surface-canvas border-border-subtle">
          <h4 className="font-bold text-text-primary mb-1.5 flex items-center gap-1">
            <span>📋</span>
            {isHi ? "दैनिक संकल्प नियम व आचार:" : "Daily Sankalp Discipline:"}
          </h4>
          <ul className="list-disc list-inside space-y-1 text-text-secondary">
            {(isHi ? data.guidelinesHi : data.guidelinesEn).map((g, idx) => (
              <li key={idx}>{g}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
